import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Plus, 
  StickyNote, 
  Calendar, 
  Trash2, 
  Edit2, 
  Check, 
  AlertCircle,
  Clock,
  Bell,
  ClipboardList
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomerOrdersTab } from "@/components/notes/CustomerOrdersTab";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotesList = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [priority, setPriority] = useState("normal");
  const [category, setCategory] = useState("");
  
  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes`, {
        params: { include_completed: showCompleted }
      });
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchNotes();
  }, [showCompleted]);
  
  const resetForm = () => {
    setTitle("");
    setContent("");
    setReminderDate("");
    setPriority("normal");
    setCategory("");
    setEditingNote(null);
  };
  
  const handleOpenAdd = () => {
    resetForm();
    setShowAddDialog(true);
  };
  
  const handleEdit = (note) => {
    setTitle(note.title);
    setContent(note.content || "");
    setReminderDate(note.reminder_date || "");
    setPriority(note.priority || "normal");
    setCategory(note.category || "");
    setEditingNote(note);
    setShowAddDialog(true);
  };
  
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    try {
      const data = {
        title: title.trim(),
        content: content.trim(),
        reminder_date: reminderDate || null,
        priority,
        category: category.trim()
      };
      
      if (editingNote) {
        await saveWithRetry(() => axios.put(`${API}/notes/${editingNote.id}`, data));
        toast.success("Note updated");
      } else {
        await saveWithRetry(() => axios.post(`${API}/notes`, data));
        toast.success("Note created");
      }
      
      setShowAddDialog(false);
      resetForm();
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error(errorMessage(error, "Failed to save note"));
    }
  };
  
  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    
    try {
      await saveWithRetry(() => axios.delete(`${API}/notes/${noteId}`));
      toast.success("Note deleted");
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error(errorMessage(error, "Failed to delete note"));
    }
  };
  
  const handleToggleComplete = async (noteId) => {
    try {
      await saveWithRetry(() => axios.post(`${API}/notes/${noteId}/toggle-complete`));
      fetchNotes();
    } catch (error) {
      console.error("Error toggling note:", error);
      toast.error(errorMessage(error, "Failed to update note"));
    }
  };
  
  const getStatusBadge = (note) => {
    if (note.completed) {
      return <Badge className="bg-green-500">Completed</Badge>;
    }
    
    switch (note.status) {
      case "overdue":
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Overdue</Badge>;
      case "today":
        return <Badge className="bg-orange-500"><Bell className="h-3 w-3 mr-1" />Today</Badge>;
      case "upcoming":
        return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />Upcoming</Badge>;
      default:
        return null;
    }
  };
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "border-l-red-500";
      case "low": return "border-l-gray-300";
      default: return "border-l-blue-500";
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // Group notes by status
  const overdueNotes = notes.filter(n => n.status === "overdue" && !n.completed);
  const todayNotes = notes.filter(n => n.status === "today" && !n.completed);
  const upcomingNotes = notes.filter(n => n.status === "upcoming" && !n.completed);
  const noDateNotes = notes.filter(n => n.status === "no_date" && !n.completed);
  const completedNotes = notes.filter(n => n.completed);

  return (
    <div className="p-6 space-y-6" data-testid="notes-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <StickyNote className="h-8 w-8 text-yellow-500" />
          <div>
            <h1 className="text-2xl font-bold">Notes & Reminders</h1>
            <p className="text-gray-500 text-sm">Keep track of tasks and reminders</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox 
              checked={showCompleted} 
              onCheckedChange={setShowCompleted}
            />
            Show completed
          </label>
          <Button onClick={handleOpenAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Note
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading notes...</div>
      ) : notes.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <StickyNote className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No notes yet. Create your first note!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Overdue Section */}
          {overdueNotes.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-red-600 mb-3 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Overdue ({overdueNotes.length})
              </h2>
              <div className="grid gap-3">
                {overdueNotes.map(note => (
                  <NoteCard 
                    key={note.id} 
                    note={note} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggleComplete}
                    getStatusBadge={getStatusBadge}
                    getPriorityColor={getPriorityColor}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Today Section */}
          {todayNotes.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-orange-600 mb-3 flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Due Today ({todayNotes.length})
              </h2>
              <div className="grid gap-3">
                {todayNotes.map(note => (
                  <NoteCard 
                    key={note.id} 
                    note={note} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggleComplete}
                    getStatusBadge={getStatusBadge}
                    getPriorityColor={getPriorityColor}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Upcoming Section */}
          {upcomingNotes.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-blue-600 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Upcoming ({upcomingNotes.length})
              </h2>
              <div className="grid gap-3">
                {upcomingNotes.map(note => (
                  <NoteCard 
                    key={note.id} 
                    note={note} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggleComplete}
                    getStatusBadge={getStatusBadge}
                    getPriorityColor={getPriorityColor}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* No Date Section */}
          {noDateNotes.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-600 mb-3 flex items-center gap-2">
                <StickyNote className="h-5 w-5" />
                Notes ({noDateNotes.length})
              </h2>
              <div className="grid gap-3">
                {noDateNotes.map(note => (
                  <NoteCard 
                    key={note.id} 
                    note={note} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggleComplete}
                    getStatusBadge={getStatusBadge}
                    getPriorityColor={getPriorityColor}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Completed Section */}
          {showCompleted && completedNotes.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-green-600 mb-3 flex items-center gap-2">
                <Check className="h-5 w-5" />
                Completed ({completedNotes.length})
              </h2>
              <div className="grid gap-3 opacity-60">
                {completedNotes.map(note => (
                  <NoteCard 
                    key={note.id} 
                    note={note} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggleComplete}
                    getStatusBadge={getStatusBadge}
                    getPriorityColor={getPriorityColor}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingNote ? "Edit Note" : "New Note"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What do you need to remember?"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Details</label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Add more details..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Reminder Date</label>
                <Input
                  type="date"
                  value={reminderDate}
                  onChange={(e) => setReminderDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Category (optional)</label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Stock, Supplier, Customer"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingNote ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Note Card Component
const NoteCard = ({ note, onEdit, onDelete, onToggle, getStatusBadge, getPriorityColor, formatDate }) => {
  return (
    <Card className={`border-l-4 ${getPriorityColor(note.priority)} ${note.completed ? 'bg-gray-50' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Checkbox 
              checked={note.completed}
              onCheckedChange={() => onToggle(note.id)}
              className="mt-1"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`font-medium ${note.completed ? 'line-through text-gray-500' : ''}`}>
                  {note.title}
                </h3>
                {getStatusBadge(note)}
                {note.category && (
                  <Badge variant="outline" className="text-xs">{note.category}</Badge>
                )}
                {note.priority === "high" && !note.completed && (
                  <Badge className="bg-red-100 text-red-700 text-xs">High Priority</Badge>
                )}
              </div>
              {note.content && (
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{note.content}</p>
              )}
              {note.reminder_date && (
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(note.reminder_date)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onEdit(note)}
              className="h-8 w-8 p-0"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onDelete(note.id)}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Notes = () => (
  <Tabs defaultValue={localStorage.getItem("notes_active_tab") || "notes"} onValueChange={(v) => localStorage.setItem("notes_active_tab", v)} className="pt-4">
    <TabsList className="mx-6" data-testid="notes-tabs">
      <TabsTrigger value="notes" data-testid="notes-tab-reminders"><StickyNote className="h-4 w-4 mr-2" />Notes & Reminders</TabsTrigger>
      <TabsTrigger value="orders" data-testid="notes-tab-customer-orders"><ClipboardList className="h-4 w-4 mr-2" />Customer Orders</TabsTrigger>
    </TabsList>
    <TabsContent value="notes"><NotesList /></TabsContent>
    <TabsContent value="orders"><CustomerOrdersTab /></TabsContent>
  </Tabs>
);

export default Notes;
